import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FileText, Calendar, User, Building2, Clock,
  CheckCircle, AlertCircle, AlertTriangle, Paperclip,
  Hash, Flag, ChevronDown, ChevronUp, Send, Edit,
  Upload, History, Users, Stamp, XCircle, ArrowRightLeft,
  Info, Play, Eye
} from 'lucide-react';
import api from '../services/api';
import { getDateLocale } from '../utils/i18nHelpers';
import './DocumentDetail.css';

const RESOLUTION_TYPE_COLORS = {
  approve:  { color: '#22c55e', icon: CheckCircle },
  reject:   { color: '#ef4444', icon: XCircle },
  rework:   { color: '#f59e0b', icon: AlertCircle },
  delegate: { color: '#3b82f6', icon: ArrowRightLeft },
  info:     { color: '#64748b', icon: Info },
  execute:  { color: '#8b5cf6', icon: Play },
};

function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['documents', 'common']);

  const getResolutionConfig = (type) => {
    const colors = RESOLUTION_TYPE_COLORS[type] || RESOLUTION_TYPE_COLORS.info;
    return {
      ...colors,
      label: t(`documents:resolution.types.${type}`, type),
    };
  };
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  const [statuses, setStatuses] = useState([]);

  // Модал смены статуса
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null); // { type: 'document' | 'item', id }
  const [newStatus, setNewStatus] = useState('');
  const [statusComment, setStatusComment] = useState('');

  // Модал выполнения исполнителем
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [resultText, setResultText] = useState('');
  const [completeFile, setCompleteFile] = useState(null);

  // Загрузка файлов
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  // Резолюции
  const [resolutions, setResolutions] = useState([]);
  const [deptList, setDeptList] = useState([]);
  const [showResModal, setShowResModal] = useState(false);
  const [resType, setResType] = useState('');
  const [resText, setResText] = useState('');
  const [resDelegatedTo, setResDelegatedTo] = useState('');
  const [resDeadline, setResDeadline] = useState('');
  const [resSubmitting, setResSubmitting] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const leadershipRoles = ['admin', 'director', 'deputy_director'];
  const isAdmin = user.is_staff || user.is_superuser || leadershipRoles.includes(user.role);
  const userDeptId = user.department_id || user.department;

  const fetchResolutions = useCallback(async () => {
    try {
      const res = await api.getResolutions({ content_type: 'document', object_id: id });
      setResolutions(res.data?.results || res.data || []);
    } catch { /* ignore */ }
  }, [id]);

  useEffect(() => {
    fetchDocument();
    fetchStatuses();
    fetchResolutions();
    api.getDepartments().then(res => {
      setDeptList(res.data?.results || res.data || []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      const res = await api.getDocument(id);
      setDocument(res.data);

      // Автораскрытие ВСЕХ пунктов — сотрудник должен видеть полную картину
      if (res.data.items) {
        const autoExpand = {};
        res.data.items.forEach(item => {
          autoExpand[item.id] = true;
        });
        setExpandedItems(autoExpand);
      }

      setLoading(false);
    } catch (err) {
      console.error('Ошибка загрузки документа:', err);
      setError(t('documents:messages.loadDocError'));
      setLoading(false);
    }
  };

  const fetchStatuses = async () => {
    try {
      const res = await api.getDocumentStatuses();
      setStatuses(res.data.results || res.data);
    } catch (err) {
      console.error('Ошибка загрузки статусов:', err);
    }
  };

  const toggleItem = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleChangeStatus = async () => {
    if (!newStatus) return;
    try {
      if (statusTarget.type === 'document') {
        await api.changeDocumentStatus(statusTarget.id, {
          status: parseInt(newStatus),
          comment: statusComment
        });
      } else {
        await api.changeItemStatus(statusTarget.id, {
          status: parseInt(newStatus),
          comment: statusComment
        });
      }
      setShowStatusModal(false);
      setNewStatus('');
      setStatusComment('');
      setStatusTarget(null);
      fetchDocument();
    } catch (err) {
      alert('Ошибка: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleComplete = async () => {
    try {
      await api.completeExecution(completeTarget.id, {
        result_text: resultText
      });

      // Если выбран файл — загружаем его к документу
      if (completeFile && completeTarget._item) {
        const formData = new FormData();
        formData.append('file', completeFile);
        formData.append('document', completeTarget._item.document);
        formData.append('file_name', completeFile.name);
        formData.append('description', t('documents:detail.resultDescription', { number: completeTarget._item.item_number }));
        await api.uploadDocumentFile(formData);
      }

      setShowCompleteModal(false);
      setResultText('');
      setCompleteFile(null);
      setCompleteTarget(null);
      fetchDocument();
    } catch (err) {
      alert('Ошибка: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUploadFile = async () => {
    if (!uploadFile || !document) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('document', document.id);
      formData.append('file_name', uploadFile.name);
      formData.append('description', uploadDescription);
      await api.uploadDocumentFile(formData);
      setUploadFile(null);
      setUploadDescription('');
      setShowUploadForm(false);
      setUploading(false);
      fetchDocument();
    } catch (err) {
      setUploading(false);
      alert('Ошибка загрузки: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCreateResolution = async () => {
    if (!resType || !resText.trim()) return;
    try {
      setResSubmitting(true);
      const data = {
        content_type_name: 'document',
        object_id: parseInt(id),
        resolution_type: resType,
        text: resText,
      };
      if (resDelegatedTo) data.delegated_to = parseInt(resDelegatedTo);
      if (resDeadline) data.deadline = resDeadline;

      await api.createResolution(data);
      setShowResModal(false);
      setResType('');
      setResText('');
      setResDelegatedTo('');
      setResDeadline('');
      fetchResolutions();
    } catch (err) {
      alert('Ошибка: ' + (err.response?.data?.detail || JSON.stringify(err.response?.data) || err.message));
    } finally {
      setResSubmitting(false);
    }
  };

  const handleExecuteResolution = async (resId) => {
    try {
      await api.executeResolution(resId);
      fetchResolutions();
    } catch (err) {
      alert('Ошибка: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openStatusModal = (type, itemId) => {
    setStatusTarget({ type, id: type === 'document' ? document.id : itemId });
    setShowStatusModal(true);
  };

  const openCompleteModal = (executor) => {
    setCompleteTarget(executor);
    setShowCompleteModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(getDateLocale(), {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(getDateLocale(), {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="doc-detail-page">
        <div className="loading-spinner">{t('documents:misc.loadingDocument')}</div>
      </div>
    );
  }

  // Собираем невыполненные поручения текущего пользователя/отдела
  const myPendingExecutors = [];
  const allExecutors = [];
  if (document && document.items) {
    document.items.forEach(item => {
      if (item.executors) {
        item.executors.forEach(ex => {
          allExecutors.push({ ...ex, _item: item });
          if (
            ex.status !== 'completed' &&
            (ex.department === userDeptId || ex.executor === user.id)
          ) {
            myPendingExecutors.push({ ...ex, _item: item });
          }
        });
      }
    });
  }

  // Соисполнители — все отделы кроме моего
  const coExecutorDepts = [];
  if (allExecutors.length > 0) {
    const seen = new Set();
    allExecutors.forEach(ex => {
      if (ex.department !== userDeptId && ex.department_name && !seen.has(ex.department)) {
        seen.add(ex.department);
        coExecutorDepts.push(ex.department_name);
      }
    });
  }

  // Общая история исполнения со всех пунктов
  const allHistory = [];
  if (document && document.items) {
    document.items.forEach(item => {
      if (item.history) {
        item.history.forEach(h => {
          allHistory.push({ ...h, _itemNumber: item.item_number, _itemText: item.text });
        });
      }
    });
  }
  allHistory.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Проверка: является ли текущий пользователь исполнителем данного пункта
  const isMyExecutor = (exec) => {
    return exec.department === userDeptId || exec.executor === user.id;
  };

  // Отдел пользователя — ответственный за документ
  const isResponsibleDept = document && document.responsible_department === userDeptId;

  // Есть ли хотя бы один пункт с текстом
  const hasItems = document && document.items && document.items.length > 0;

  if (error || !document) {
    return (
      <div className="doc-detail-page">
        <div className="error-state">
          <AlertCircle size={48} />
          <h3>{error || t('documents:messages.notFound')}</h3>
          <button className="btn-back-large" onClick={() => navigate('/documents')}>
            {t('documents:detail.backToDocuments')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="doc-detail-page">
      {/* Шапка */}
      <div className="doc-detail-header">
        <div className="doc-detail-header-actions">
          {isAdmin && (
            <>
              <button className="btn-action btn-edit" onClick={() => navigate(`/documents/${document.id}/edit`)}>
                <Edit size={16} /> {t('common:buttons.edit')}
              </button>
              <button className="btn-action btn-status" onClick={() => openStatusModal('document', document.id)}>
                <Send size={16} /> {t('documents:detail.changeStatus')}
              </button>
            </>
          )}
          {(isAdmin || user.role === 'department_head') && (
            <button className="btn-action btn-resolution" onClick={() => setShowResModal(true)}>
              <Stamp size={16} /> {t('documents:resolution.title')}
            </button>
          )}
        </div>
      </div>

      {/* Баннер исполнителя — мои поручения */}
      {myPendingExecutors.length > 0 && (
        <div className="executor-action-banner">
          <div className="executor-banner-header">
            <AlertTriangle size={20} />
            <span>{t('documents:misc.pendingTasksBanner', { count: myPendingExecutors.length })}</span>
          </div>
          {myPendingExecutors.map(ex => (
            <div key={ex.id} className="executor-banner-item">
              <div className="executor-banner-info">
                <span className="executor-banner-item-num">{t('documents:items.itemNumber')} {ex._item.item_number}</span>
                <span className="executor-banner-item-text">{ex._item.text}</span>
                {ex.deadline && (
                  <span className="executor-banner-deadline">
                    <Calendar size={13} /> {t('documents:items.deadline')}: {formatDate(ex.deadline)}
                  </span>
                )}
              </div>
              <button
                className="btn-executor-complete"
                onClick={() => openCompleteModal(ex)}
              >
                <CheckCircle size={16} /> {t('common:buttons.complete')}
              </button>
            </div>
          ))}
          {coExecutorDepts.length > 0 && (
            <div className="co-executors-info">
              <Users size={14} />
              <span>{t('documents:executors.coExecutors')}: {coExecutorDepts.join(', ')}</span>
            </div>
          )}
        </div>
      )}

      {/* Основная карточка */}
      <div className="doc-detail-card">
        <div className="doc-detail-title-row">
          <div className="doc-reg-number">
            <Hash size={16} />
            {document.registration_number}
          </div>
          <span
            className="doc-status-badge"
            style={{ backgroundColor: document.status_color || '#94a3b8', color: '#fff' }}
          >
            {document.status_name}
          </span>
          {document.priority_name && (
            <span
              className="priority-badge"
              style={{ backgroundColor: document.priority_color || '#94a3b8', color: '#fff' }}
            >
              <Flag size={12} /> {document.priority_name}
            </span>
          )}
        </div>

        <h1 className="doc-detail-title">{document.title}</h1>

        <div className="doc-detail-meta">
          <div className="meta-item">
            <FileText size={16} />
            <span>{document.document_type_name}</span>
          </div>
          {document.authority_name && (
            <div className="meta-item">
              <Building2 size={16} />
              <span>{t('documents:detail.sourceLabel')}: {document.authority_name}</span>
            </div>
          )}
          <div className="meta-item">
            <Building2 size={16} />
            <span>{t('documents:detail.responsibleDept')}: {document.responsible_department_name}</span>
          </div>
          <div className="meta-item">
            <User size={16} />
            <span>{t('documents:detail.registeredBy')}: {document.registered_by_name}</span>
          </div>
          <div className="meta-item">
            <Clock size={16} />
            <span>{t('documents:detail.created')}: {formatDateTime(document.created_at)}</span>
          </div>
          {document.incoming_number && (
            <div className="meta-item">
              <Hash size={16} />
              <span>{t('documents:detail.incomingNumberFrom', { number: document.incoming_number, date: formatDate(document.incoming_date) })}</span>
            </div>
          )}
        </div>

        {/* Дедлайн */}
        {document.deadline && (
          <div className={`deadline-banner ${document.is_overdue ? 'overdue' : ''}`}>
            <Calendar size={18} />
            <span>
              {t('documents:detail.executionDeadline')}: <strong>{formatDate(document.deadline)}</strong>
              {document.is_overdue && (
                <span className="overdue-text">
                  <AlertTriangle size={14} /> {t('documents:detail.overdueBy', { days: Math.abs(document.days_until_deadline) })}
                </span>
              )}
              {!document.is_overdue && document.days_until_deadline !== null && (
                <span className="days-left"> ({t('documents:detail.daysLeft', { days: document.days_until_deadline })})</span>
              )}
            </span>
          </div>
        )}

        {document.completed_at && (
          <div className="completed-banner">
            <CheckCircle size={18} />
            <span>{t('documents:detail.completed')}: {formatDateTime(document.completed_at)}</span>
          </div>
        )}
      </div>

      {/* Резолюции */}
      {resolutions.length > 0 && (
        <div className="doc-section resolutions-section">
          <h2><Stamp size={18} /> {t('documents:resolution.title')} ({resolutions.length})</h2>
          <div className="resolutions-list">
            {resolutions.map(res => {
              const cfg = getResolutionConfig(res.resolution_type);
              const Icon = cfg.icon;
              return (
                <div key={res.id} className={`resolution-card ${res.is_executed ? 'executed' : ''}`}>
                  <div className="resolution-card-header">
                    <span className="resolution-type-badge" style={{ background: cfg.color }}>
                      <Icon size={14} /> {cfg.label}
                    </span>
                    <span className="resolution-author">{res.author_name}</span>
                    {res.author_position && (
                      <span className="resolution-position">{res.author_position}</span>
                    )}
                    <span className="resolution-date">{formatDateTime(res.created_at)}</span>
                  </div>
                  <p className="resolution-text">{res.text}</p>
                  <div className="resolution-meta">
                    {res.delegated_to_name && (
                      <span className="resolution-delegate">
                        <Building2 size={13} /> {res.delegated_to_name}
                      </span>
                    )}
                    {res.deadline && (
                      <span className="resolution-deadline">
                        <Calendar size={13} /> {t('documents:resolution.executionDeadline')}: {formatDate(res.deadline)}
                      </span>
                    )}
                    {res.is_executed ? (
                      <span className="resolution-executed">
                        <CheckCircle size={13} /> {t('documents:resolution.executed')} {res.executed_at ? formatDate(res.executed_at) : ''}
                      </span>
                    ) : (
                      (res.resolution_type === 'execute' || res.resolution_type === 'delegate' || res.resolution_type === 'rework') && (
                        <button
                          className="btn-small btn-execute-res"
                          onClick={() => handleExecuteResolution(res.id)}
                        >
                          <Eye size={14} /> {t('documents:resolution.executed')}
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Содержание — показываем всегда */}
      <div className="doc-section">
        <h2><FileText size={18} /> {t('documents:detail.contentSection')}</h2>
        {document.description ? (
          <p className="doc-description">{document.description}</p>
        ) : (
          <p className="empty-note">{t('documents:messages.descriptionNotSet')}</p>
        )}
      </div>

      {/* Блок задания для ответственного отдела (если нет пунктов/исполнителей) */}
      {!hasItems && isResponsibleDept && !isAdmin && (
        <div className="assignment-card">
          <div className="assignment-card-header">
            <AlertTriangle size={20} />
            <h3>{t('documents:misc.assignmentForDepartment')}</h3>
          </div>
          <p className="assignment-card-text">
            {t('documents:misc.assignmentDescription', { department: document.responsible_department_name })}
            {' '}{t('documents:misc.assignmentDescriptionNoItems')}
          </p>
          <p className="assignment-card-hint">
            {t('documents:misc.assignmentHint')}
          </p>
        </div>
      )}

      {/* Пункты документа — показываем всегда */}
      {hasItems ? (
        <div className="doc-section">
          <h2><FileText size={18} /> {t('documents:items.title')} ({document.items.length})</h2>
          <div className="items-list">
            {document.items.map(item => (
              <div key={item.id} className={`item-card ${item.is_overdue ? 'item-overdue' : ''}`}>
                <div className="item-header" onClick={() => toggleItem(item.id)}>
                  <div className="item-header-left">
                    <span className="item-number">{t('documents:items.itemNumber')} {item.item_number}</span>
                    <span
                      className="item-status-badge"
                      style={{ backgroundColor: item.status_color || '#94a3b8', color: '#fff' }}
                    >
                      {item.status_name}
                    </span>
                    {item.is_overdue && (
                      <AlertTriangle size={14} className="item-overdue-icon" />
                    )}
                  </div>
                  <div className="item-header-right">
                    {item.deadline && (
                      <span className={`item-deadline ${item.is_overdue ? 'overdue' : ''}`}>
                        <Calendar size={14} /> {formatDate(item.deadline)}
                      </span>
                    )}
                    {/* Быстрая кнопка «Выполнено» в заголовке пункта */}
                    {item.executors && item.executors.some(ex =>
                      ex.status !== 'completed' && isMyExecutor(ex)
                    ) && (
                      <button
                        className="btn-small btn-complete"
                        onClick={e => {
                          e.stopPropagation();
                          const myEx = item.executors.find(ex =>
                            ex.status !== 'completed' && isMyExecutor(ex)
                          );
                          if (myEx) openCompleteModal({ ...myEx, _item: item });
                        }}
                      >
                        <CheckCircle size={14} /> {t('common:buttons.complete')}
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        className="btn-small btn-status-change"
                        onClick={e => { e.stopPropagation(); openStatusModal('item', item.id); }}
                      >
                        {t('documents:detail.status')}
                      </button>
                    )}
                    {expandedItems[item.id] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </div>

                <p className="item-text">{item.text}</p>

                {expandedItems[item.id] && (
                  <div className="item-details">
                    {/* Исполнители */}
                    {item.executors && item.executors.length > 0 && (
                      <div className="executors-section">
                        <h4>{t('documents:executors.title')}</h4>
                        <div className="executors-list">
                          {item.executors.map(exec => (
                            <div key={exec.id} className={`executor-row ${exec.status === 'completed' ? 'completed' : ''}`}>
                              <div className="executor-info">
                                <span className="executor-dept">
                                  <Building2 size={14} /> {exec.department_name}
                                </span>
                                {exec.executor_name && (
                                  <span className="executor-name">
                                    <User size={14} /> {exec.executor_name}
                                  </span>
                                )}
                                {exec.is_responsible && (
                                  <span className="responsible-badge">{t('documents:executors.responsible')}</span>
                                )}
                              </div>
                              <div className="executor-status-info">
                                <span className={`executor-status status-${exec.status}`}>
                                  {exec.status_display}
                                </span>
                                {exec.deadline && (
                                  <span className="executor-deadline">
                                    <Calendar size={12} /> {formatDate(exec.deadline)}
                                  </span>
                                )}
                                {exec.completion_date && (
                                  <span className="executor-completed">
                                    <CheckCircle size={12} /> {formatDate(exec.completion_date)}
                                  </span>
                                )}
                              </div>
                              <div className="executor-actions">
                                {exec.status !== 'completed' && (
                                  <button
                                    className="btn-small btn-complete"
                                    onClick={() => openCompleteModal({ ...exec, _item: item })}
                                  >
                                    <CheckCircle size={14} /> {t('common:buttons.complete')}
                                  </button>
                                )}
                              </div>
                              {exec.result_text && (
                                <div className="executor-result">
                                  <strong>{t('documents:detail.report')}:</strong> {exec.result_text}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* История пункта */}
                    {item.history && item.history.length > 0 && (
                      <div className="history-section">
                        <h4>{t('documents:detail.historyTitle')}</h4>
                        <div className="history-list">
                          {item.history.map(h => (
                            <div key={h.id} className="history-item">
                              <div className="history-dot" />
                              <div className="history-content">
                                <div className="history-header">
                                  <strong>{h.user_name}</strong>
                                  <span className="history-date">{formatDateTime(h.created_at)}</span>
                                </div>
                                <p className="history-action">{h.action}</p>
                                {h.old_status && h.new_status && (
                                  <p className="history-status-change">
                                    {h.old_status} → {h.new_status}
                                  </p>
                                )}
                                {h.comment && <p className="history-comment">{h.comment}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : !isAdmin && (
        <div className="doc-section">
          <h2><FileText size={18} /> {t('documents:items.title')}</h2>
          <p className="empty-note">{t('documents:items.noItems')}</p>
        </div>
      )}

      {/* Файлы */}
      <div className="doc-section">
        <div className="section-header-row">
          <h2><Paperclip size={18} /> {t('documents:detail.attachedFiles')}</h2>
          <button
            className="btn-small btn-upload-toggle"
            onClick={() => setShowUploadForm(!showUploadForm)}
          >
            <Upload size={14} /> {t('documents:detail.attachFile')}
          </button>
        </div>

        {showUploadForm && (
          <div className="upload-form">
            <div className="form-group">
              <label>{t('documents:detail.file')}</label>
              <input
                type="file"
                className="form-control"
                onChange={e => setUploadFile(e.target.files[0] || null)}
              />
            </div>
            <div className="form-group">
              <label>{t('documents:detail.fileDescriptionOptional')}</label>
              <input
                type="text"
                className="form-control"
                placeholder={t('documents:detail.fileDescription')}
                value={uploadDescription}
                onChange={e => setUploadDescription(e.target.value)}
              />
            </div>
            <div className="upload-actions">
              <button
                className="btn-primary"
                onClick={handleUploadFile}
                disabled={!uploadFile || uploading}
              >
                {uploading ? t('documents:detail.uploading') : t('documents:detail.upload')}
              </button>
              <button
                className="btn-secondary"
                onClick={() => { setShowUploadForm(false); setUploadFile(null); setUploadDescription(''); }}
              >
                {t('common:buttons.cancel')}
              </button>
            </div>
          </div>
        )}

        {document.files && document.files.length > 0 ? (
          <div className="files-list">
            {document.files.map(file => (
              <a
                key={file.id}
                href={file.file_url || file.file}
                target="_blank"
                rel="noopener noreferrer"
                className="file-item"
              >
                <Paperclip size={16} />
                <div className="file-info-col">
                  <span className="file-name">{file.file_name}</span>
                  {file.description && (
                    <span className="file-desc">{file.description}</span>
                  )}
                </div>
                {file.file_size > 0 && (
                  <span className="file-size">
                    {(file.file_size / 1024).toFixed(0)} КБ
                  </span>
                )}
              </a>
            ))}
          </div>
        ) : (
          <p className="empty-note">{t('documents:detail.noFiles')}</p>
        )}
      </div>

      {/* Общая история исполнения */}
      {allHistory.length > 0 && (
        <div className="doc-section">
          <h2><History size={18} /> {t('documents:history.title')}</h2>
          <div className="history-list all-history">
            {allHistory.map((h, idx) => (
              <div key={h.id || idx} className="history-item">
                <div className="history-dot" />
                <div className="history-content">
                  <div className="history-header">
                    <strong>{h.user_name}</strong>
                    <span className="history-item-ref">п. {h._itemNumber}</span>
                    <span className="history-date">{formatDateTime(h.created_at)}</span>
                  </div>
                  <p className="history-action">{h.action}</p>
                  {h.old_status && h.new_status && (
                    <p className="history-status-change">
                      {h.old_status} → {h.new_status}
                    </p>
                  )}
                  {h.comment && <p className="history-comment">{h.comment}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Модал смены статуса */}
      {showStatusModal && (
        <div className="modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('documents:detail.changeStatus')}</h2>
              <button className="modal-close" onClick={() => setShowStatusModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('documents:detail.newStatus')}</label>
                <select
                  className="form-control"
                  value={newStatus}
                  onChange={e => setNewStatus(e.target.value)}
                >
                  <option value="">{t('documents:detail.selectStatus')}</option>
                  {statuses.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('documents:detail.comment')}</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder={t('documents:detail.statusChangeReason')}
                  value={statusComment}
                  onChange={e => setStatusComment(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowStatusModal(false)}>{t('common:buttons.cancel')}</button>
              <button className="btn-primary" onClick={handleChangeStatus} disabled={!newStatus}>
                {t('common:buttons.apply')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модал выполнения */}
      {showCompleteModal && completeTarget && (
        <div className="modal-overlay" onClick={() => { setShowCompleteModal(false); setCompleteFile(null); }}>
          <div className="modal-content modal-complete" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('documents:executors.markComplete')}</h2>
              <button className="modal-close" onClick={() => { setShowCompleteModal(false); setCompleteFile(null); }}>×</button>
            </div>
            <div className="modal-body">
              {/* Информация о пункте */}
              {completeTarget._item && (
                <div className="complete-modal-info">
                  <div className="complete-modal-item-num">{t('documents:detail.itemPoint', { number: completeTarget._item.item_number })}</div>
                  <div className="complete-modal-item-text">{completeTarget._item.text}</div>
                  {completeTarget.deadline && (
                    <div className="complete-modal-deadline">
                      <Calendar size={14} /> {t('documents:items.deadline')}: {formatDate(completeTarget.deadline)}
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>{t('documents:executors.completionReport')}</label>
                <textarea
                  className="form-control"
                  rows="4"
                  placeholder={t('documents:executors.completionPlaceholder')}
                  value={resultText}
                  onChange={e => setResultText(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>{t('documents:executors.attachFile')}</label>
                <input
                  type="file"
                  className="form-control"
                  onChange={e => setCompleteFile(e.target.files[0] || null)}
                />
                {completeFile && (
                  <span className="file-selected">
                    <Paperclip size={12} /> {completeFile.name}
                  </span>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowCompleteModal(false); setCompleteFile(null); }}>{t('common:buttons.cancel')}</button>
              <button className="btn-primary" onClick={handleComplete}>
                <CheckCircle size={16} /> {t('documents:executors.markComplete')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Модал резолюции */}
      {showResModal && (
        <div className="modal-overlay" onClick={() => setShowResModal(false)}>
          <div className="modal-content modal-resolution" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Stamp size={20} /> {t('documents:resolution.addResolution')}</h2>
              <button className="modal-close" onClick={() => setShowResModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('documents:resolution.resolutionType')} *</label>
                <div className="resolution-type-grid">
                  {Object.entries(RESOLUTION_TYPE_COLORS).map(([key, cfg]) => {
                    const RIcon = cfg.icon;
                    return (
                      <button
                        key={key}
                        className={`res-type-btn ${resType === key ? 'active' : ''}`}
                        style={resType === key ? { borderColor: cfg.color, background: cfg.color + '15' } : {}}
                        onClick={() => setResType(key)}
                      >
                        <RIcon size={16} style={{ color: cfg.color }} />
                        <span>{t(`documents:resolution.types.${key}`)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <label>{t('documents:resolution.resolutionText')} *</label>
                <textarea
                  className="form-control"
                  rows="4"
                  placeholder={t('documents:resolution.resolutionTextPlaceholder')}
                  value={resText}
                  onChange={e => setResText(e.target.value)}
                />
              </div>

              {(resType === 'delegate' || resType === 'execute') && (
                <div className="form-group">
                  <label>{t('documents:resolution.delegateTo')}</label>
                  <select
                    className="form-control"
                    value={resDelegatedTo}
                    onChange={e => setResDelegatedTo(e.target.value)}
                  >
                    <option value="">{t('documents:resolution.selectDepartment')}</option>
                    {deptList.map(d => (
                      <option key={d.id} value={d.id}>{d.name_ru || d.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>{t('documents:resolution.executionDeadline')}</label>
                <input
                  type="date"
                  className="form-control"
                  value={resDeadline}
                  onChange={e => setResDeadline(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowResModal(false)}>{t('common:buttons.cancel')}</button>
              <button
                className="btn-primary"
                onClick={handleCreateResolution}
                disabled={!resType || !resText.trim() || resSubmitting}
              >
                {resSubmitting ? t('documents:resolution.saving') : t('documents:resolution.addResolution')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentDetail;
